import React, { useState } from "react";
import { Box, Text } from "@twilio-paste/core";
import { WarningIcon } from "@twilio-paste/icons/esm/WarningIcon";
import type { ConversationCustomerDto } from "../../../dto/conversation";
import { CustomerMatchesModal } from "../../modals/CustomerMatchesModal";

type Props = {
  phone?: string | null;
  customers: ConversationCustomerDto[];
};

function extractE164(phone?: string | null) {
  const raw = (phone ?? "").trim();
  if (!raw) return "";
  const afterColon = raw.includes(":") ? raw.split(":").pop() ?? raw : raw;
  return afterColon.replace(/[^\d+]/g, "");
}

function fullName(c: ConversationCustomerDto) {
  return `${c.name ?? ""} ${c.surname ?? ""}`.trim();
}

export const TitleRow: React.FC<Props> = ({ phone, customers }) => {
  const count = customers?.length ?? 0;
  const multipleCustomers = count > 1;

  const [isModalOpen, setModalOpen] = useState(false);

  if (count === 1) {
    const c = customers[0];
    return (
      <Text
        as="h3"
        marginBottom="space20"
        fontWeight="fontWeightSemibold"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
      >
        {c.code} - {fullName(c)}
      </Text>
    );
  }

  const e164 = extractE164(phone);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        alignContent="center"
        columnGap="space30"
      >
        <Text
          as={"h3"}
          marginBottom="space20"
          fontWeight="fontWeightSemibold"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          textDecoration="none"
          color={multipleCustomers ? "colorTextWarningStrong" : "colorText"}
        >
          {e164 || "Numero sconosciuto"}
        </Text>

        {multipleCustomers ? (
          <Box marginBottom="space20">
            <ButtonLikeIcon onPress={openModal} />
          </Box>
        ) : null}
      </Box>

      <CustomerMatchesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        customers={customers}
      />
    </>
  );
};

const ButtonLikeIcon: React.FC<{ onPress: () => void }> = ({ onPress }) => {
  const handleClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPress();
  };

  const handleKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onPress();
    }
  };

  return (
    <Box
      as="span"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        display: "inline-flex",
        alignItems: "center",
        cursor: "pointer",
      }}
      aria-label="Mostra contatti CRM associati"
    >
      <WarningIcon decorative={false} title="Contatti CRM multipli" />
    </Box>
  );
};
